import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, FileText, Edit2, Trash2, Wrench, Search } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { NORWEGIAN_REGIONS } from '../lib/regions';
import CustomerFormFields from '../components/CustomerFormFields';
import CustomerCombobox from '../components/CustomerCombobox';
import { InstallationForm } from './InstallationForm';

export const Montering = () => {
  const [installations, setInstallations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [custSearch, setCustSearch] = useState('');
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', contact_person: '', phone: '', email: '', address: '', postal_code: '', region: '', door_code: '', gate_code: '', alarm_code: '', access_info: '', cleaning_interval: '4', notes: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [instRes, custRes] = await Promise.all([
        axios.get(`${API}/installations`),
        axios.get(`${API}/customers`),
      ]);
      setInstallations(instRes.data || []);
      setCustomers(custRes.data || []);
    } catch {
      toast.error('Kunne ikke hente monteringer');
    } finally {
      setLoading(false);
    }
  };

  const customerMap = customers.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});

  const openNew = () => {
    setEditId(null);
    setSelectedCustomerId('');
    setCustSearch('');
    setShowNewCustomer(false);
    setNewCustomer({ name: '', contact_person: '', phone: '', email: '', address: '', postal_code: '', region: '', door_code: '', gate_code: '', alarm_code: '', access_info: '', cleaning_interval: '4', notes: '' });
    setFormOpen(true);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error('Kundenavn er påkrevd');
      return;
    }
    setCreatingCustomer(true);
    try {
      const res = await axios.post(`${API}/customers`, {
        name: newCustomer.name.trim(),
        contact_person: newCustomer.contact_person || '',
        phone: newCustomer.phone || '',
        email: newCustomer.email || '',
        address: newCustomer.address || '',
        postal_code: newCustomer.postal_code || '',
        region: newCustomer.region || '',
        door_code: newCustomer.door_code || '',
        gate_code: newCustomer.gate_code || '',
        alarm_code: newCustomer.alarm_code || '',
        access_info: newCustomer.access_info || '',
        cleaning_interval: newCustomer.cleaning_interval || '4',
        notes: newCustomer.notes || '',
      });
      setCustomers((prev) => [...prev, res.data]);
      setSelectedCustomerId(res.data.id);
      setShowNewCustomer(false);
      toast.success('Kunde opprettet');
    } catch {
      toast.error('Kunne ikke opprette kunde');
    } finally {
      setCreatingCustomer(false);
    }
  };

  const openEdit = (inst) => {
    setEditId(inst.id);
    setSelectedCustomerId(inst.customer_id);
    setFormOpen(true);
  };

  const handleSaved = () => {
    setFormOpen(false);
    setEditId(null);
    setSelectedCustomerId('');
    setCustSearch('');
    setShowNewCustomer(false);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/installations/${deleteId}`);
      toast.success('Montering slettet');
      setDeleteId(null);
      fetchData();
    } catch {
      toast.error('Kunne ikke slette montering');
    }
  };

  const downloadPdf = async (instId) => {
    try {
      const response = await axios.get(`${API}/installations/${instId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `montering_${instId}.pdf`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
      toast.success('Monterings-PDF lastet ned');
    } catch {
      toast.error('Kunne ikke laste ned PDF');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try { return format(new Date(d), 'dd. MMM yyyy', { locale: nb }); } catch { return d; }
  };

  const selectedCustomer = customerMap[selectedCustomerId];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Laster...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Montering
          </h1>
          <p className="text-slate-600">Monteringsskjemaer med utstyr og signatur</p>
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white shrink-0" data-testid="new-montering-button">
          <Plus className="h-4 w-4 mr-2" />
          Ny montering
        </Button>
      </div>

      {installations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Wrench className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Ingen monteringer registrert</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {installations.map((inst) => {
            const cust = customerMap[inst.customer_id];
            return (
              <Card key={inst.id} className="hover:shadow-md transition-shadow" data-testid={`montering-${inst.id}`}>
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold text-lg text-slate-900">{cust?.name || 'Ukjent kunde'}</span>
                        <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded-full">
                          {(inst.equipment?.length || 0)} enheter
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 flex flex-wrap gap-x-4 gap-y-1">
                        <span><span className="text-slate-400">Dato:</span> {formatDate(inst.date)}</span>
                        <span><span className="text-slate-400">Tekniker:</span> {inst.technician_name || '-'}</span>
                        {inst.signature_name && <span><span className="text-slate-400">Signert av:</span> {inst.signature_name}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(inst.id)} className="text-slate-700" data-testid={`montering-pdf-${inst.id}`}>
                        <FileText className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">PDF</span>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(inst)} className="text-slate-600" data-testid={`montering-edit-${inst.id}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteId(inst.id)} className="text-red-600 hover:bg-red-50" data-testid={`montering-delete-${inst.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editId ? 'Rediger montering' : 'Ny montering'}</DialogTitle>
            <DialogDescription>
              {editId ? 'Oppdater monteringsskjemaet.' : 'Velg kunde og fyll ut monteringsskjemaet.'}
            </DialogDescription>
          </DialogHeader>

          {!editId && !selectedCustomerId && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Velg kunde *</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowNewCustomer((v) => !v)} data-testid="montering-new-customer-toggle">
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Ny kunde
                </Button>
              </div>

              {showNewCustomer ? (
                <div className="space-y-2 p-3 border border-slate-200 rounded-lg bg-slate-50">
                  <CustomerFormFields
                    data={newCustomer}
                    onChange={(field, value) => setNewCustomer({ ...newCustomer, [field]: value })}
                    showCleaningInterval
                  />
                  <div className="flex gap-2 justify-end pt-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewCustomer(false)}>Avbryt</Button>
                    <Button type="button" size="sm" onClick={handleCreateCustomer} disabled={creatingCustomer} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-customer-save">
                      {creatingCustomer ? 'Oppretter...' : 'Opprett og velg'}
                    </Button>
                  </div>
                </div>
              ) : (
                <CustomerCombobox
                  customers={customers}
                  value={selectedCustomerId}
                  onChange={setSelectedCustomerId}
                  placeholder="Søk og velg kunde..."
                  testId="montering-customer-select"
                />
              )}
            </div>
          )}

          {!editId && selectedCustomerId && (
            <div className="flex items-center justify-between p-2.5 bg-brand-50 border border-brand-200 rounded-lg">
              <span className="text-sm font-medium text-slate-800">{selectedCustomer?.name}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedCustomerId('')} data-testid="montering-change-customer">Bytt kunde</Button>
            </div>
          )}

          {selectedCustomerId && (
            <InstallationForm
              customerId={selectedCustomerId}
              customerName={selectedCustomer?.name}
              customerAddress={selectedCustomer?.address}
              installationId={editId}
              onSaved={handleSaved}
              onCancel={() => setFormOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slett montering?</AlertDialogTitle>
            <AlertDialogDescription>
              Er du sikker på at du vil slette denne monteringen? Denne handlingen kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Montering;
